import React from 'react';

/**
 * LegalplusLogo — marca oficial (águila heráldica + wordmark).
 *
 *   variant="full"     → águila + wordmark completo (hero, login)
 *   variant="wordmark" → águila + LEGALPLUS en horizontal (header expandido)
 *   variant="mark"     → solo águila (sidebar compacto, favicon-like)
 *
 *   tone:
 *     "light"  → logo plateado original, apto para fondos oscuros (default)
 *     "dark"   → logo entintado en marino, apto para fondos claros
 *
 *   halo={true} → aura sutil indigo detrás (solo contexto hero)
 *
 * Los activos residen en /public:
 *   legalplus-logo-light.png        (1600px, master retina)
 *   legalplus-logo-light@800.png    (800px, default)
 *   legalplus-logo-light@256.png    (256px, sidebar/compact)
 *   legalplus-logo-dark.png         (variante para fondos claros)
 *   legalplus-mark-512.png / @192 / @64 (solo águila)
 */

// Los assets viven en /public/brand/ para evitar colisión con el proxy
// NPM que enruta /legalplus/* al backend (el prefijo "legalplus-" del
// filename capturaba los requests y devolvía 404).
const ASSETS = {
  full: {
    light: {
      src: '/brand/legalplus-logo-light@800.png',
      srcSet: '/brand/legalplus-logo-light@256.png 256w, /brand/legalplus-logo-light@800.png 800w, /brand/legalplus-logo-light.png 1600w',
      ratio: 1615 / 948,
    },
    dark: {
      src: '/brand/legalplus-logo-dark.png',
      srcSet: '/brand/legalplus-logo-dark.png 1600w',
      ratio: 1615 / 948,
    },
  },
  mark: {
    light: {
      src: '/brand/legalplus-mark-192.png',
      srcSet: '/brand/legalplus-mark-64.png 64w, /brand/legalplus-mark-192.png 192w, /brand/legalplus-mark-512.png 512w',
      ratio: 1,
    },
    dark: {
      src: '/brand/legalplus-mark-192.png',
      srcSet: '/brand/legalplus-mark-64.png 64w, /brand/legalplus-mark-192.png 192w, /brand/legalplus-mark-512.png 512w',
      ratio: 1,
      invert: true,
    },
  },
};

const LogoImg = ({ asset, height, alt = 'Legalplus', style, className = '', invert = false }) => {
  const width = Math.round(height * asset.ratio);
  return (
    <img
      src={asset.src}
      srcSet={asset.srcSet}
      sizes={`${width}px`}
      alt={alt}
      width={width}
      height={height}
      loading="eager"
      decoding="async"
      draggable={false}
      className={`select-none ${className}`}
      style={{
        height,
        width,
        objectFit: 'contain',
        filter: invert ? 'invert(1) hue-rotate(180deg) saturate(0.9)' : undefined,
        ...style,
      }}
    />
  );
};

export const LegalplusMark = ({ size = 40, tone = 'light', className = '', ...rest }) => {
  const asset = ASSETS.mark[tone] || ASSETS.mark.light;
  return (
    <span className={`inline-flex items-center justify-center ${className}`} {...rest}>
      <LogoImg asset={asset} height={size} alt="Legalplus" invert={asset.invert} />
    </span>
  );
};

export const LegalplusWordmark = ({ height = 32, tone = 'light', className = '', ...rest }) => {
  // El PNG "full" ya trae el águila + wordmark; para "wordmark" usamos el mismo con height menor.
  const asset = ASSETS.full[tone] || ASSETS.full.light;
  return (
    <span className={`inline-flex items-center ${className}`} {...rest}>
      <LogoImg asset={asset} height={height} alt="Legalplus · Partners S.A.S" />
    </span>
  );
};

export const LegalplusLogo = ({
  variant = 'full',
  tone = 'light',
  size = 'md',
  halo = false,
  className = '',
  ...rest
}) => {
  const heights = { sm: 28, md: 44, lg: 72, xl: 120, '2xl': 180 };
  const h = heights[size] || heights.md;

  if (variant === 'mark') {
    return <LegalplusMark size={h} tone={tone} className={className} {...rest} />;
  }

  const asset = ASSETS.full[tone] || ASSETS.full.light;

  const content = <LogoImg asset={asset} height={h} alt="Legalplus · Partners S.A.S" />;

  if (halo) {
    return (
      <span
        className={`relative inline-flex items-center justify-center ${className}`}
        {...rest}
      >
        <span
          aria-hidden
          className="absolute inset-0 -z-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(60% 60% at 50% 50%, rgba(79,125,255,0.28) 0%, rgba(92,225,230,0.10) 45%, transparent 75%)',
            filter: 'blur(28px)',
            transform: 'scale(1.35)',
          }}
        />
        <span className="relative z-10">{content}</span>
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center justify-center ${className}`} {...rest}>
      {content}
    </span>
  );
};

export default LegalplusLogo;
